import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import type {
    CarrierCancelRequest,
    CarrierDispatchRequest,
    CarrierLegacyUnitParams,
    CarrierLegacyUnitSearchParams,
    CarrierQuoteRequest,
    CarrierReturnDecisionRequest,
} from '@/lib/api/carrier-shipment-api'

import { ApiError } from '@/lib/api/client'
import {
    CARRIER_SHIPMENT_NOT_FOUND,
    carrierShipmentApi,
} from '@/lib/api/carrier-shipment-api'
import { orderKeys } from '@/hooks/use-sales-order-query'
import { m } from '@/lib/paraglide/messages'

export const carrierShipmentKeys = {
    all: ['carrier-shipments'] as const,
    detail: (orderId: string) =>
        [...carrierShipmentKeys.all, 'detail', orderId] as const,
    legacyUnits: (orderId: string, params: CarrierLegacyUnitParams) =>
        [...carrierShipmentKeys.all, 'legacy-units', orderId, params] as const,
    legacySearch: (orderId: string, params: CarrierLegacyUnitSearchParams) =>
        [...carrierShipmentKeys.all, 'legacy-search', orderId, params] as const,
}

/**
 * The order's live carrier shipment, or null when it has none — which is the
 * normal answer for every internally delivered order, so the 404 is data, not
 * a failure.
 *
 * Status arrives by webhook, so the panel polls instead of reacting: a slow
 * refetch keeps a parcel's progress current without any effect wiring.
 */
export function useCarrierShipment(orderId: string, enabled = true) {
    return useQuery({
        queryKey: carrierShipmentKeys.detail(orderId),
        queryFn: async () => {
            try {
                const res = await carrierShipmentApi.get(orderId)
                return res.data ?? null
            } catch (err) {
                if (
                    err instanceof ApiError &&
                    err.status === 404 &&
                    err.code === CARRIER_SHIPMENT_NOT_FOUND
                )
                    return null
                throw err
            }
        },
        enabled,
        // A cancelled shipment is finished; anything else may still move.
        refetchInterval: (query) =>
            query.state.data && query.state.data.status !== 'cancelled'
                ? 60_000
                : false,
    })
}

/**
 * The old communes the printed label can name for the order's ward, with the
 * one to pre-select. `params` previews receiver edits not yet saved on the
 * order; an empty `candidates` list means the form has nothing new to show.
 */
export function useCarrierLegacyUnits(
    orderId: string,
    params: CarrierLegacyUnitParams,
    enabled = true,
) {
    return useQuery({
        queryKey: carrierShipmentKeys.legacyUnits(orderId, params),
        queryFn: async () => {
            const res = await carrierShipmentApi.legacyUnits(orderId, params)
            return res.data ?? null
        },
        enabled,
    })
}

/** Old communes of the province named like `params.q`; idle below two letters. */
export function useCarrierLegacySearch(
    orderId: string,
    params: CarrierLegacyUnitSearchParams,
) {
    return useQuery({
        queryKey: carrierShipmentKeys.legacySearch(orderId, params),
        queryFn: async () => {
            const res = await carrierShipmentApi.searchLegacyUnits(
                orderId,
                params,
            )
            return res.data ?? []
        },
        enabled: params.q.trim().length >= 2,
        staleTime: 5 * 60 * 1000,
    })
}

/** Prices every service the carrier sells to this order's destination. */
export function useCarrierQuote(orderId: string) {
    return useMutation({
        mutationFn: async (body: CarrierQuoteRequest) => {
            const res = await carrierShipmentApi.quote(orderId, body)
            return res.data ?? []
        },
    })
}

/**
 * Books the waybill. The caller handles the failure: a 422 asks for a manual
 * carrier-unit pick and must stay on the form, so it cannot be swallowed by a
 * generic toast here.
 */
export function useDispatchCarrierShipment(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (body: CarrierDispatchRequest) => {
            const res = await carrierShipmentApi.dispatch(orderId, body)
            return res.data
        },
        onSuccess: (data) => {
            if (data) {
                qc.setQueryData(carrierShipmentKeys.detail(orderId), data)
            }
            void qc.invalidateQueries({
                queryKey: carrierShipmentKeys.detail(orderId),
            })
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(orderId) })
            toast.success(m.carrier_form_submit_success())
        },
    })
}

/**
 * Withdraws the waybill. The carrier's refusal (already collected) and the
 * unknown-state 409 both surface to the caller, which renders the action the
 * operator can take; only an unexpected failure gets the generic toast.
 */
export function useCancelCarrierShipment(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (body: CarrierCancelRequest) => {
            await carrierShipmentApi.cancel(orderId, body)
        },
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: carrierShipmentKeys.detail(orderId),
            })
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(orderId) })
            toast.success(m.carrier_cancel_success())
        },
    })
}

export function useResolveCarrierReturn(orderId: string) {
    const qc = useQueryClient()
    return useMutation({
        mutationFn: async (body: CarrierReturnDecisionRequest) => {
            await carrierShipmentApi.resolveReturn(orderId, body)
        },
        onSuccess: () => {
            void qc.invalidateQueries({
                queryKey: carrierShipmentKeys.detail(orderId),
            })
            void qc.invalidateQueries({ queryKey: orderKeys.detail(orderId) })
            void qc.invalidateQueries({ queryKey: orderKeys.timeline(orderId) })
            toast.success(m.carrier_return_decision_success())
        },
    })
}

/**
 * Fetches the label link on demand. The carrier's print code carries its own
 * expiry, so a stored URL would rot — every print asks for a fresh one.
 */
export function useCarrierLabel(orderId: string) {
    return useMutation({
        mutationFn: async () => {
            const res = await carrierShipmentApi.label(orderId)
            const labelURL = res.data?.label_url?.trim() ?? ''
            if (!labelURL) {
                throw new Error('EMPTY_CARRIER_LABEL_URL')
            }
            const parsed = new URL(labelURL)
            if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
                throw new Error('INVALID_CARRIER_LABEL_URL')
            }
            return labelURL
        },
        onError: () => {
            toast.error(m.carrier_label_failed())
        },
    })
}
